/**
 * 音频任务节点 - 展示语音合成任务的状态和结果
 * 自动轮询更新任务状态，展示生成的音频
 */
import React, { memo, useState, useEffect, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import { NodeLayout } from './NodeLayout';
import voiceService from '../../../api/VoiceService';
import { useProject } from '../providers/ProjectProvider';
import { toast } from 'react-toastify';
import './AudioTaskNode.css';

export const AudioTaskNode = memo(({ id, data }) => {
  const { updateNodeData } = useReactFlow();
  const { readOnly } = useProject();
  const [status, setStatus] = useState(data.status || 'pending');
  const [audioUrl, setAudioUrl] = useState(data.audioUrl || null);
  const [error, setError] = useState(data.error || null);
  const [inputParams, setInputParams] = useState(data.inputParams || {});
  const [outputParams, setOutputParams] = useState(data.outputParams || null);
  const [isTransferring, setIsTransferring] = useState(false); // 音频转存状态
  const [sunoMusicList, setSunoMusicList] = useState(data.sunoMusicList || null); // Suno 音乐列表
  const pollingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const hasCheckedTransferRef = useRef(false); // 标记是否已检查过转存状态
  const previousTransferStatusRef = useRef(null); // 记录上一次的转存状态，用于检测状态变化

  const taskId = data.taskId;
  const taskType = data.taskType || 'text-to-audio';

  // 轮询查询任务状态
  const pollTaskStatus = async () => {
    if (!taskId || !mountedRef.current) return;

    try {
      const result = await voiceService.getTaskInfo(taskId);

      if (!mountedRef.current) return;

      if (result) {
        const newStatus = result.status;
        setStatus(newStatus);
        
        // VoiceService.getTaskInfo 已经解析了 input_params 和 output_params
        setInputParams(result.input_params || {});
        setOutputParams(result.output_params || null);

        if (newStatus === 'completed') {
          // 任务完成，提取音频 URL
          console.log('任务完成，完整结果:', result);
          console.log('输出参数 output_params:', result.output_params);
          console.log('输入参数 input_params:', result.input_params);
          
          const outputData = result.output_params;
          
          // 尝试从多个可能的位置提取音频 URL
          let audioURL = null;
          let sunoMusicList = null; // Suno 可能返回多首音乐
          
          if (outputData) {
            // 方式1: output_params.data.audio (Minimax 语音合成格式)
            if (outputData.data && outputData.data.audio) {
              audioURL = outputData.data.audio;
              console.log('从 output_params.data.audio 找到音频:', audioURL);
            }
            // 方式2: output_params.audio (有些API直接在根级)
            else if (outputData.audio) {
              audioURL = outputData.audio;
              console.log('从 output_params.audio 找到音频:', audioURL);
            }
            // 方式3: Suno 音乐格式 - output_params.data.data[].audio_url
            else if (outputData.data && Array.isArray(outputData.data.data) && outputData.data.data.length > 0) {
              // Suno 返回的是音乐数组，取第一首作为主音频
              const musicData = outputData.data.data;
              sunoMusicList = musicData;
              
              // 找到第一个有 audio_url 的音乐
              const firstMusic = musicData.find(m => m.audio_url);
              if (firstMusic) {
                audioURL = firstMusic.audio_url;
                console.log('从 Suno output_params.data.data[].audio_url 找到音频:', audioURL);
                console.log('Suno 共生成', musicData.length, '首音乐');
                
                // 保存完整的音乐列表
                setSunoMusicList(musicData);
              }
            }
          }
          
          if (audioURL) {
            setAudioUrl(audioURL);
            setError(null);
            console.log('✅ 音频URL已设置:', audioURL);
            
            // 检查音频是否已经转存到自己的服务器
            const isAudioTransferred = audioURL && (audioURL.startsWith('https://st-video.cc') || audioURL.startsWith('https://cdn-video.51sux.com') || audioURL.startsWith('https://videos-jp.ss3.life'));
            
            // 标记已完成转存检查
            hasCheckedTransferRef.current = true;
            
            if (isAudioTransferred) {
              // 音频已转存，停止轮询
              console.log(`AudioTaskNode ${id} - 音频已转存到 st-video.cc，停止轮询`);
              setIsTransferring(false);
              toast.success('语音合成完成！');
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              
              // 更新转存状态记录
              previousTransferStatusRef.current = true;
            } else {
              // 音频还未转存，继续轮询但降低频率到30秒
              console.log(`AudioTaskNode ${id} - 音频还未转存，继续30秒轮询`);
              setIsTransferring(true);
              
              // 更新转存状态记录
              previousTransferStatusRef.current = false;
              
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              // 设置30秒轮询
              pollingIntervalRef.current = setInterval(() => {
                pollTaskStatus();
              }, 30000); // 30秒
            }
          } else {
            console.error('❌ 未找到音频URL');
            console.error('完整的 output_params 结构:', JSON.stringify(outputData, null, 2));
            setError('未能获取音频文件');
            
            // 停止轮询
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        } else if (newStatus === 'failed') {
          const outputData = result.output_params;
          const errorMsg = result.error || outputData?.error || outputData?.base_resp?.status_msg || '生成失败';
          setError(errorMsg);
          
          // 停止轮询
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }
    } catch (err) {
      console.error('查询任务状态失败:', err);
    }
  };

  // 同步状态到节点数据
  useEffect(() => {
    updateNodeData(id, {
      status,
      audioUrl,
      error,
      inputParams,
      outputParams,
      sunoMusicList,
    });
  }, [id, status, audioUrl, error, inputParams, outputParams, sunoMusicList, updateNodeData]);

  // 启动轮询
  useEffect(() => {
    mountedRef.current = true;

    if (!taskId) return;

    // 如果任务还在进行中，启动快速轮询（5秒）
    if (status === 'pending' || status === 'processing') {
      // 如果已经有轮询，不重复创建
      if (!pollingIntervalRef.current) {
        console.log(`AudioTaskNode ${id} - 启动任务状态轮询（5秒）`);
        hasCheckedTransferRef.current = false; // 重置转存检查标记
        
        // 立即查询一次
        pollTaskStatus();
        
        // 启动定时轮询（每5秒）
        pollingIntervalRef.current = setInterval(() => {
          pollTaskStatus();
        }, 5000);
      }
    }
    // 如果任务已完成，检查是否需要音频转存轮询
    else if (status === 'completed') {
      // 如果没有音频URL，立即查询一次（处理秒完成的情况）
      if (!audioUrl && !pollingIntervalRef.current) {
        console.log(`AudioTaskNode ${id} - 任务已完成但无音频URL，立即查询`);
        pollTaskStatus();
        return; // 查询后会触发状态更新，重新进入这个 useEffect
      }
      
      // 如果有音频URL，检查是否需要转存轮询
      if (audioUrl && !hasCheckedTransferRef.current && !pollingIntervalRef.current) {
        hasCheckedTransferRef.current = true; // 标记已检查
        
        const needsTransfer = checkAudioTransfer(audioUrl);
        
        if (needsTransfer) {
          console.log(`AudioTaskNode ${id} - 页面加载时检测到音频未转存，启动30秒轮询`);
          setIsTransferring(true);
          
          // 初始化转存状态为false（未转存）
          previousTransferStatusRef.current = false;
          
          // 立即查询一次
          pollTaskStatus();
          
          // 启动慢速轮询（每30秒）
          pollingIntervalRef.current = setInterval(() => {
            pollTaskStatus();
          }, 30000);
        } else {
          console.log(`AudioTaskNode ${id} - 页面加载时检测到音频已转存，无需轮询`);
          setIsTransferring(false);
          
          // 页面刷新时，音频已转存，设置状态为true
          previousTransferStatusRef.current = true;
        }
      }
    }

    // 清理函数
    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [taskId, status, audioUrl]);

  // 检查音频是否需要转存
  const checkAudioTransfer = (audioUrl) => {
    if (!audioUrl) return false;
    
    const isAudioTransferred = audioUrl.startsWith('https://st-video.cc') || audioUrl.startsWith('https://cdn-video.51sux.com');
    
    return !isAudioTransferred; // 返回是否需要转存
  };

  // 手动刷新
  const handleRefresh = () => {
    pollTaskStatus();
    toast.info('正在刷新任务状态...');
  };

  // 下载音频
  const handleDownload = () => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `audio_${taskId.slice(0, 8)}.${inputParams.audio_setting?.format || 'mp3'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 获取状态标签
  const getStatusBadge = () => {
    const statusConfig = {
      pending: { text: '排队中', className: 'AudioTaskNode-status-pending' },
      processing: { text: '生成中', className: 'AudioTaskNode-status-processing' },
      completed: { text: '已完成', className: 'AudioTaskNode-status-completed' },
      failed: { text: '失败', className: 'AudioTaskNode-status-failed' },
    };

    const config = statusConfig[status] || { text: status, className: '' };
    
    return (
      <span className={`AudioTaskNode-status-badge ${config.className}`}>
        {config.text}
      </span>
    );
  };

  return (
    <NodeLayout 
      id={id} 
      type="audioTask" 
      title="语音合成任务"
      showInputHandle={true}
      showOutputHandle={true}
    >
      <div className="AudioTaskNode-wrapper">
        {/* 任务信息头部 */}
        <div className="AudioTaskNode-header">
          <div className="AudioTaskNode-task-id">
            任务ID: {taskId ? taskId.slice(0, 8) + '...' : 'N/A'}
          </div>
          {getStatusBadge()}
        </div>
        {Number.isFinite(Number(data.chargedPoints)) && (
          <div className="AudioTaskNode-charge">
            已预扣 {Number(data.chargedPoints).toLocaleString('zh-CN')} 积分
          </div>
        )}

        {/* 音频转存提示（静默） */}
        {isTransferring && status === 'completed' && (
          <div className="AudioTaskNode-transfer-hint">
            🔄 音频转存中...
          </div>
        )}

        {/* 输入参数 */}
        {inputParams && Object.keys(inputParams).length > 0 && (
          <div className="AudioTaskNode-params">
            <div className="AudioTaskNode-params-title">参数</div>
            <div className="AudioTaskNode-params-grid">
              {inputParams.model && (
                <div className="AudioTaskNode-param-item">
                  <span className="AudioTaskNode-param-label">模型:</span>
                  <span className="AudioTaskNode-param-value">{inputParams.model}</span>
                </div>
              )}
              {inputParams.voice_setting?.voice_id && (
                <div className="AudioTaskNode-param-item">
                  <span className="AudioTaskNode-param-label">音色:</span>
                  <span className="AudioTaskNode-param-value">
                    {inputParams.voice_setting.voice_id.substring(0, 20)}
                    {inputParams.voice_setting.voice_id.length > 20 ? '...' : ''}
                  </span>
                </div>
              )}
              {inputParams.voice_setting?.speed && (
                <div className="AudioTaskNode-param-item">
                  <span className="AudioTaskNode-param-label">语速:</span>
                  <span className="AudioTaskNode-param-value">{inputParams.voice_setting.speed}x</span>
                </div>
              )}
              {inputParams.audio_setting?.format && (
                <div className="AudioTaskNode-param-item">
                  <span className="AudioTaskNode-param-label">格式:</span>
                  <span className="AudioTaskNode-param-value">
                    {inputParams.audio_setting.format.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            {inputParams.text && (
              <div className="AudioTaskNode-text">
                <div className="AudioTaskNode-text-label">文本:</div>
                <div className="AudioTaskNode-text-content">
                  {inputParams.text.length > 100 
                    ? inputParams.text.substring(0, 100) + '...' 
                    : inputParams.text}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 进行中状态 */}
        {(status === 'pending' || status === 'processing') && (
          <div className="AudioTaskNode-loading">
            <div className="AudioTaskNode-spinner" />
            <div className="AudioTaskNode-loading-text">
              {status === 'pending' ? '任务排队中，请稍候...' : '正在生成语音...'}
            </div>
            <button 
              onClick={handleRefresh}
              disabled={readOnly}
              className="AudioTaskNode-refresh-button"
            >
              🔄 刷新状态
            </button>
          </div>
        )}

        {/* 调试信息 */}
        {status === 'completed' && !audioUrl && (
          <div className="AudioTaskNode-error" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
            <div style={{ fontSize: '12px', color: '#856404' }}>
              ⚠️ 调试信息：
              <br />
              状态: {status}
              <br />
              音频URL: {audioUrl || '未设置'}
              <br />
              输出参数: {outputParams ? '已设置' : '未设置'}
              <br />
              {outputParams && (
                <div style={{ marginTop: '5px', fontSize: '11px', maxHeight: '100px', overflow: 'auto', background: '#f8f9fa', padding: '5px', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(outputParams, null, 2)}
                </div>
              )}
              <button 
                onClick={handleRefresh}
                disabled={readOnly}
                className="AudioTaskNode-refresh-button"
                style={{ marginTop: '8px' }}
              >
                🔄 刷新任务状态
              </button>
            </div>
          </div>
        )}

        {/* 生成成功 */}
        {status === 'completed' && audioUrl && (
          <div className="AudioTaskNode-results">
            <div className="AudioTaskNode-results-title">
              {sunoMusicList ? `生成结果 (${sunoMusicList.length}首)` : '生成结果'}
            </div>
            
            {/* Suno 多首音乐展示 */}
            {sunoMusicList && sunoMusicList.length > 0 ? (
              <div className="AudioTaskNode-suno-list">
                {sunoMusicList.map((music, index) => (
                  <div key={music.id || index} className="AudioTaskNode-suno-item">
                    {/* 封面图 */}
                    {music.image_url && (
                      <div className="AudioTaskNode-suno-cover">
                        <img src={music.image_url} alt={music.title || `音乐 ${index + 1}`} />
                      </div>
                    )}
                    
                    {/* 音乐信息 */}
                    <div className="AudioTaskNode-suno-info">
                      <div className="AudioTaskNode-suno-title">
                        {music.title || `音乐 ${index + 1}`}
                      </div>
                      {music.tags && (
                        <div className="AudioTaskNode-suno-tags">{music.tags}</div>
                      )}
                      {music.duration > 0 && (
                        <div className="AudioTaskNode-suno-duration">
                          时长: {Math.floor(music.duration / 60)}:{(music.duration % 60).toString().padStart(2, '0')}
                        </div>
                      )}
                    </div>
                    
                    {/* 音频播放器 */}
                    {music.audio_url && (
                      <audio
                        src={music.audio_url}
                        preload="metadata"
                        controls
                        className="AudioTaskNode-audio-control"
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* 普通单音频播放器 */
              <div className="AudioTaskNode-player">
                <audio
                  src={audioUrl}
                  preload="metadata"
                  controls
                  className="AudioTaskNode-audio-control"
                />

                <div className="AudioTaskNode-player-footer">
                  <div className="AudioTaskNode-player-meta">
                    <div className="AudioTaskNode-player-status">已就绪</div>
                    {outputParams?.extra_info?.audio_length && (
                      <div className="AudioTaskNode-player-duration">
                        时长: {(outputParams.extra_info.audio_length / 1000).toFixed(1)}秒
                      </div>
                    )}
                    {outputParams?.extra_info?.audio_size && (
                      <div className="AudioTaskNode-player-duration">
                        大小: {(outputParams.extra_info.audio_size / 1024).toFixed(2)} KB
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleDownload}
                    className="AudioTaskNode-download-button"
                    disabled={readOnly}
                    title="下载音频"
                  >
                    📥
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 失败状态 */}
        {status === 'failed' && (
          <div className="AudioTaskNode-error">
            <div className="AudioTaskNode-error-icon">❌</div>
            <div className="AudioTaskNode-error-text">
              {error || '生成失败，请检查参数后重试'}
            </div>
            <button 
              onClick={handleRefresh}
              disabled={readOnly}
              className="AudioTaskNode-retry-button"
            >
              🔄 重新查询
            </button>
          </div>
        )}

        {/* 输出信息 */}
        {outputParams?.extra_info && (
          <div className="AudioTaskNode-metrics">
            <div className="AudioTaskNode-metrics-title">详细信息</div>
            <div className="AudioTaskNode-metrics-grid">
              {outputParams.extra_info.audio_length && (
                <div className="AudioTaskNode-metric-item">
                  <span className="AudioTaskNode-metric-label">时长:</span>
                  <span className="AudioTaskNode-metric-value">
                    {(outputParams.extra_info.audio_length / 1000).toFixed(2)}秒
                  </span>
                </div>
              )}
              {outputParams.extra_info.audio_size && (
                <div className="AudioTaskNode-metric-item">
                  <span className="AudioTaskNode-metric-label">大小:</span>
                  <span className="AudioTaskNode-metric-value">
                    {(outputParams.extra_info.audio_size / 1024).toFixed(2)} KB
                  </span>
                </div>
              )}
              {outputParams.extra_info.audio_sample_rate && (
                <div className="AudioTaskNode-metric-item">
                  <span className="AudioTaskNode-metric-label">采样率:</span>
                  <span className="AudioTaskNode-metric-value">
                    {outputParams.extra_info.audio_sample_rate} Hz
                  </span>
                </div>
              )}
              {outputParams.extra_info.bitrate && (
                <div className="AudioTaskNode-metric-item">
                  <span className="AudioTaskNode-metric-label">比特率:</span>
                  <span className="AudioTaskNode-metric-value">
                    {(outputParams.extra_info.bitrate / 1000).toFixed(0)} kbps
                  </span>
                </div>
              )}
              {outputParams.extra_info.usage_characters && (
                <div className="AudioTaskNode-metric-item">
                  <span className="AudioTaskNode-metric-label">字符数:</span>
                  <span className="AudioTaskNode-metric-value">
                    {outputParams.extra_info.usage_characters}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </NodeLayout>
  );
});

AudioTaskNode.displayName = 'AudioTaskNode';
